import { SelectedFile } from "../../../shared/components/ResumeUploader/hooks/SelectedFile";
import { useFilePicker } from "../../../shared/components/ResumeUploader/hooks/useFilePicker";
import { useResumeFileUpload } from "../../../shared/components/ResumeUploader/hooks/useResumeFileUpload";
import {
  FileInputValidity,
  getUploadError,
  type UploadError,
  validateSelectedFile,
} from "../../../shared/components/ResumeUploader/hooks/validateSelectedFile";
import { useI18n } from "../../../shared/I18n";

import { useCallback, useEffect, useRef, useState } from "react";

type UploadResult = {
  fileId: string;
  fileName: string;
};

/**
 * Composes the shared file-picker + resume-upload hooks for the gated Match
 * Overview "Upload your resume" CTA (RN-4783). Mirrors the composition used by
 * Homepage's `useHomepageResumeUpload`, trimmed to this screen's needs — no
 * dual-resume pre-delete and no refetchQueries, since the gate-flip hydration
 * is deferred to RN-4784.
 */
export function useMatchOverviewResumeUpload() {
  const { gettext } = useI18n();
  const { pickFile, file: pickedFile, clearFile, error: pickerError } = useFilePicker();
  const { uploadResumeFile, loading, progress } = useResumeFileUpload();
  const [error, setError] = useState<UploadError | undefined>(undefined);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const isUploading = useRef(false);

  const clearError = useCallback(() => setError(undefined), []);
  const clearUploadResult = useCallback(() => setUploadResult(null), []);

  const triggerUpload = useCallback(async () => {
    // A file picked mid-upload would be ignored by the effect below and then wiped
    // by the in-flight upload's clearFile(), so don't open the picker at all.
    if (isUploading.current) {
      return;
    }

    await pickFile();
  }, [pickFile]);

  // Surfaces failures from the native file picker itself (e.g. a missing/broken
  // document-picker module) — otherwise these are swallowed inside useFilePicker
  // and the CTA appears to silently do nothing.
  useEffect(() => {
    if (!pickerError) {
      return;
    }

    console.error("[useMatchOverviewResumeUpload] File picker failed:", pickerError);
    setError(getUploadError(FileInputValidity.UNKNOWN_ERROR, gettext));
  }, [pickerError, gettext]);

  useEffect(() => {
    if (!pickedFile || isUploading.current) {
      return;
    }

    const upload = async () => {
      isUploading.current = true;
      setError(undefined);

      try {
        const mimeType = pickedFile.mimeType ?? "application/octet-stream";
        const selectedFile = new SelectedFile(
          pickedFile.file !== undefined ? pickedFile.file : pickedFile.uri,
          pickedFile.name,
          pickedFile.size ?? 0,
          mimeType,
        );

        const validity = validateSelectedFile(selectedFile);
        if (validity !== FileInputValidity.VALID) {
          setError(getUploadError(validity, gettext, { fileName: selectedFile.name }));
          return;
        }

        const result = await uploadResumeFile(selectedFile, "PARSED");
        const fileId = result?.data?.addJobSeekerProfileResumeFile?.originalResumeFile?.id;

        if (fileId) {
          setUploadResult({ fileId, fileName: selectedFile.name });
        }
      } catch (uploadError) {
        console.error("[useMatchOverviewResumeUpload] Upload failed:", uploadError);
        setError(getUploadError(FileInputValidity.UNKNOWN_ERROR, gettext));
      } finally {
        isUploading.current = false;
        clearFile();
      }
    };

    void upload();
  }, [pickedFile, gettext, uploadResumeFile, clearFile]);

  return {
    triggerUpload,
    loading,
    progress,
    error,
    clearError,
    uploadResult,
    clearUploadResult,
  };
}
